import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { FileText, Layers, RefreshCw, RotateCw, Search, Square } from "lucide-react";

import { DataTable } from "../components/DataTable.jsx";
import { FilterBar } from "../components/FilterBar.jsx";
import { PageHeader } from "../components/PageHeader.jsx";
import { ProjectIcon } from "../components/ProjectIcon.jsx";
import { ResourceBar } from "../components/ResourceBar.jsx";
import { StatusBadge, StatusDot } from "../components/StatusBadge.jsx";
import { EmptyState, Modal, Spinner, ago, useToast } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useLive } from "../lib/events.js";
import { useUrlState, useUrlStates } from "../lib/useUrlState.js";

/**
 * Every PM2 process on the box, not only the ones ops deploys — a panel that
 * shows half the machine is worse than none.
 *
 * The header is about resources rather than runtimes: CPU, memory and disk are
 * what make a box unhappy, and they are what you check first. Which Node major
 * each process runs is still here, as a column and a filter, because the
 * migration needs it — but it is not what the top of the page is for.
 */
export function Processes() {
    const [processes, setProcesses] = useState(null);
    const [system, setSystem] = useState(null);
    const [history, setHistory] = useState(null);
    const [logs, setLogs] = useState(null);
    // In the URL, so a filtered view survives a refresh and can be sent to someone.
    const [{ q: query, status, runtime, project }, setUrl, clearUrl] = useUrlStates({ q: "", status: "", runtime: "", project: "" });
    const setQuery = (value) => setUrl("q", value);
    const setStatus = (value) => setUrl("status", value);
    const setRuntime = (value) => setUrl("runtime", value);
    const setProject = (value) => setUrl("project", value);
    // Separate from the filters on purpose: it arranges the rows rather than
    // choosing them, so "clear filters" must not switch it off.
    const [group, setGroup] = useUrlState("group", "");
    const grouped = group === "project";
    const toast = useToast();

    const load = () => {
        api.system.pm2().then(setProcesses).catch(() => setProcesses([]));
        api.system.info().then(setSystem).catch(() => {});
        api.system.history().then(setHistory).catch(() => {});
    };

    // The sampler behind the charts announces every sample it takes, so the
    // page redraws exactly when there is a new point to draw.
    useLive(["system"], load);

    /** One classification, so a row and a filter never disagree. */
    const runtimeOf = useMemo(() => {
        const byProcess = new Map();
        for (const bucket of system?.runtimes ?? []) {
            for (const name of bucket.processes) byProcess.set(name, bucket);
        }
        return (proc) => {
            const bucket = byProcess.get(proc.name);
            if (!bucket) return { key: "", text: "—", tone: "muted" };
            if (bucket.major === "26") return { key: bucket.key, text: "26", tone: "text-emerald-600 dark:text-emerald-400" };
            if (bucket.major) return { key: bucket.key, text: bucket.major, tone: "text-gray-600 dark:text-gray-400" };
            if (bucket.key === "unpinned") return { key: bucket.key, text: "unpinned", tone: "text-amber-600 dark:text-amber-400" };
            return { key: bucket.key, text: "—", tone: "muted" };
        };
    }, [system]);

    const options = useMemo(() => {
        const list = processes ?? [];
        return {
            statuses: [...new Set(list.map((p) => p.status))].filter(Boolean).sort().map((value) => ({ value, label: value })),
            projects: [
                { value: "__unmanaged", label: "Unmanaged" },
                ...[...new Set(list.map((p) => p.slug).filter(Boolean))].sort().map((value) => ({ value, label: value }))
            ],
            runtimes: (system?.runtimes ?? []).map((bucket) => ({
                value: bucket.key,
                label: `${bucket.major ? `Node ${bucket.major}` : bucket.key === "unpinned" ? "Unpinned" : bucket.key} (${bucket.count})`
            }))
        };
    }, [processes, system]);

    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return (processes ?? []).filter((proc) => {
            if (needle && !`${proc.name} ${proc.slug ?? ""}`.toLowerCase().includes(needle)) return false;
            if (status && proc.status !== status) return false;
            if (runtime && runtimeOf(proc).key !== runtime) return false;
            if (project === "__unmanaged" && proc.slug) return false;
            if (project && project !== "__unmanaged" && proc.slug !== project) return false;
            return true;
        });
    }, [processes, query, status, runtime, project, runtimeOf]);

    /**
     * The same rows, arranged by what they belong to.
     *
     * A project is several processes — an API, a worker, a web server — and the
     * flat list makes you reassemble that in your head every time. Unmanaged
     * processes are a group of their own and go last: they are the ones ops does
     * not deploy, and they are not what you came here for.
     */
    const groups = useMemo(() => {
        if (!grouped) return null;
        const byProject = new Map();
        for (const proc of shown) {
            const key = proc.slug ?? "__unmanaged";
            if (!byProject.has(key)) {
                byProject.set(key, { key, slug: proc.slug, faviconHash: proc.faviconHash, rows: [] });
            }
            byProject.get(key).rows.push(proc);
        }

        return [...byProject.values()].sort((a, b) => {
            if (a.slug === b.slug) return 0;
            if (!a.slug) return 1;
            if (!b.slug) return -1;
            return a.slug.localeCompare(b.slug);
        });
    }, [grouped, shown]);

    if (!processes) return <Spinner className="muted" />;

    const filtered = Boolean(query || status || runtime || project);
    const clear = clearUrl;

    const act = async (name, action) => {
        try {
            await api.system.pm2Action(name, action);
            toast(`${name} ${action}ed`);
            load();
        } catch (error) {
            toast(error.message, "error");
        }
    };

    const emptyState = (
                <div className="card">
                    <EmptyState
                        icon={Search}
                        title="No processes match"
                        hint="Try a different search, or clear the filters."
                        action={<button className="btn-ghost mt-2" onClick={clear}>Clear filters</button>}
                    />
                </div>
    );

    const rowActions = (proc) => [
                { label: "Reload", icon: RefreshCw, onSelect: () => act(proc.name, "reload") },
                { label: "Restart", icon: RotateCw, onSelect: () => act(proc.name, "restart") },
                { label: "Stop", icon: Square, onSelect: () => act(proc.name, "stop"), danger: true },
                { label: "Logs", icon: FileText, onSelect: () => setLogs(proc.name) }
    ];

    const columns = [
                {
                    key: "name",
                    header: "Process",
                    primary: true,
                    cell: (proc) => (
                        <span className="flex min-w-0 items-center gap-2">
                            <ProjectIcon slug={proc.slug} hash={proc.faviconHash} />
                            <span className="truncate font-mono text-xs">{proc.name}</span>
                        </span>
                    )
                },
                {
                    key: "status",
                    header: "Status",
                    // Shown on the collapsed row: whether it is up is the one thing
                    // you need before deciding to expand.
                    summary: true,
                    cell: (proc) => (
                        <>
                            <span className="md:hidden"><StatusDot status={proc.status} title={proc.status} /></span>
                            <span className="hidden md:inline"><StatusBadge status={proc.status} /></span>
                        </>
                    )
                },
                {
                    key: "memory",
                    header: "Memory",
                    align: "right",
                    summary: true,
                    cell: (proc) => <span className="tabular-nums">{(proc.memory / 1e6).toFixed(0)} MB</span>
                },
                { key: "cpu", header: "CPU", align: "right", cell: (proc) => `${proc.cpu}%` },
                {
                    key: "node",
                    header: "Node",
                    cell: (proc) => (
                        <span className={`font-mono text-xs ${runtimeOf(proc).tone}`} title={proc.interpreter ?? ""}>
                            {runtimeOf(proc).text}
                        </span>
                    )
                },
                {
                    key: "project",
                    header: "Project",
                    hideBelow: "lg",
                    cell: (proc) =>
                        proc.slug
                            ? <Link to={`/projects/${proc.slug}`} className="link">{proc.slug}</Link>
                            : <span className="muted">unmanaged</span>
                },
                {
                    key: "restarts",
                    header: "Restarts",
                    align: "right",
                    hideBelow: "xl",
                    cell: (proc) => <span className={proc.restarts > 10 ? "text-amber-600 dark:text-amber-400" : ""}>{proc.restarts}</span>
                },
                { key: "uptime", header: "Uptime", align: "right", hideBelow: "lg", cell: (proc) => <span className="muted whitespace-nowrap">{proc.uptime ? ago(proc.uptime) : "—"}</span> }
    ];

    // The project column is the group's heading when grouped; repeating it in
    // every row of that group is the same word thirty times.
    const columnsInGroup = columns.filter((column) => column.key !== "project");

    return (
        <div className="space-y-4">
            <PageHeader title="Processes" meta={`${processes.length} on this box`} />

            <ResourceBar system={system} history={history} />

            <FilterBar
                query={query}
                onQuery={setQuery}
                placeholder="Search processes"
                count={filtered ? `${shown.length} of ${processes.length}` : `${processes.length} processes`}
                toggles={[
                    { key: "group", label: "Group by project", icon: Layers, value: grouped, onChange: (on) => setGroup(on ? "project" : "") }
                ]}
                filters={[
                    { key: "status", label: "Status", value: status, onChange: setStatus, options: options.statuses },
                    { key: "runtime", label: "Runtime", value: runtime, onChange: setRuntime, options: options.runtimes },
                    { key: "project", label: "Project", value: project, onChange: setProject, options: options.projects }
                ]}
            />

            {grouped ? (
                groups.length === 0 ? (
                    emptyState
                ) : (
                    <div className="space-y-5">
                        {groups.map((section) => {
                            const online = section.rows.filter((proc) => proc.status === "online").length;
                            const memory = section.rows.reduce((total, proc) => total + (proc.memory ?? 0), 0);
                            return (
                                <section key={section.key} className="space-y-2">
                                    <header className="flex flex-wrap items-center gap-x-3 gap-y-1 px-0.5">
                                        {section.slug ? (
                                            <>
                                                <ProjectIcon slug={section.slug} hash={section.faviconHash} size={20} framed />
                                                <Link to={`/projects/${section.slug}`} className="section-title hover:underline">{section.slug}</Link>
                                            </>
                                        ) : (
                                            <span className="section-title">Unmanaged</span>
                                        )}
                                        <span className="muted text-xs tabular-nums">
                                            {section.rows.length} process{section.rows.length === 1 ? "" : "es"}
                                            {online < section.rows.length ? ` · ${section.rows.length - online} down` : ""}
                                            {memory ? ` · ${(memory / 1e6).toFixed(0)} MB` : ""}
                                        </span>
                                    </header>
                                    <DataTable
                                        rows={section.rows}
                                        rowKey={(proc) => `${proc.name}-${proc.pid}`}
                                        caption={`${section.slug ?? "Unmanaged"} processes`}
                                        actions={rowActions}
                                        columns={columnsInGroup}
                                    />
                                </section>
                            );
                        })}
                    </div>
                )
            ) : (
                <DataTable
                    rows={shown}
                    rowKey={(proc) => `${proc.name}-${proc.pid}`}
                    caption="PM2 processes"
                    empty={emptyState}
                    actions={rowActions}
                    columns={columns}
                />
            )}

            <Modal open={Boolean(logs)} onClose={() => setLogs(null)} title={`${logs} logs`} width="max-w-4xl">
                {logs ? <ProcessLogs name={logs} /> : null}
            </Modal>
        </div>
    );
}

function ProcessLogs({ name }) {
    const [text, setText] = useState(null);
    useEffect(() => {
        fetch(api.system.pm2LogsUrl(name)).then((r) => r.text()).then(setText).catch(() => setText("could not read the logs"));
    }, [name]);

    return (
        <pre className="h-96 overflow-auto rounded-lg bg-gray-900 p-3 font-mono text-xs text-gray-300">
            {text ?? "loading…"}
        </pre>
    );
}
